import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Target,
  Users,
  DollarSign,
  BarChart3,
  Settings,
  Shield,
  Truck,
  Factory,
  MessageSquare,
  TrendingUp,
  Database,
  Cog,
  Globe,
  CheckCircle,
  ArrowRight,
  Zap
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";

const MicrosoftDynamicsDetail = () => {
  const navigate = useNavigate();

  const handleGetStarted = () => {
    navigate("/");
    setTimeout(() => {
      document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const dynamicsModules = [
    { icon: Target, name: "Dynamics 365 Sales", description: "Lead qualification, opportunity management, sales pipeline automation, territory management" },
    { icon: MessageSquare, name: "Dynamics 365 Customer Service", description: "Omnichannel support, case management, knowledge base, SLA tracking" },
    { icon: BarChart3, name: "Dynamics 365 Marketing", description: "Campaign orchestration, lead nurturing, customer journey mapping, event management" },
    { icon: DollarSign, name: "Dynamics 365 Finance", description: "Financial management, budgeting, cash flow optimization, regulatory compliance" },
    { icon: Truck, name: "Dynamics 365 Supply Chain", description: "Inventory management, warehouse operations, demand planning, vendor management" },
    { icon: Factory, name: "Dynamics 365 Manufacturing", description: "Production planning, shop floor management, quality control, lean manufacturing" },
    { icon: Users, name: "Dynamics 365 Human Resources", description: "Employee lifecycle, benefits administration, performance management, talent acquisition" },
    { icon: Settings, name: "Dynamics 365 Project Operations", description: "Project management, resource allocation, time tracking, profitability analysis" },
    { icon: Globe, name: "Dynamics 365 Commerce", description: "Unified commerce, e-commerce platform, retail operations, customer engagement" },
    { icon: Cog, name: "Dynamics 365 Field Service", description: "Work order management, scheduling optimization, mobile workforce, IoT integration" },
    { icon: Database, name: "Power Platform Integration", description: "Power BI analytics, Power Apps customization, Power Automate workflows" },
    { icon: Shield, name: "Security & Compliance", description: "Role-based access, data governance, audit trails, GDPR compliance" }
  ];

  const implementationPhases = [
    {
      phase: "Discovery & Assessment",
      duration: "2-4 weeks",
      activities: ["Business process mapping", "Gap analysis", "Technical assessment", "Requirements gathering"]
    },
    {
      phase: "Design & Planning",
      duration: "3-6 weeks",
      activities: ["Solution architecture", "Data model design", "Integration planning", "Customization roadmap"]
    },
    {
      phase: "Development & Configuration",
      duration: "8-16 weeks",
      activities: ["System configuration", "Custom development", "Power Platform apps", "Integration development"]
    },
    {
      phase: "Testing & Deployment",
      duration: "4-8 weeks",
      activities: ["System testing", "User acceptance testing", "Data migration", "Go-live support"]
    }
  ];

  const benefits = [
    { metric: "360° Customer View", value: "Unified data across all touchpoints", icon: Target },
    { metric: "Process Automation", value: "Streamlined manual workflows", icon: Zap },
    { metric: "Sales Productivity", value: "Enhanced pipeline management", icon: TrendingUp },
    { metric: "Operational Efficiency", value: "Improved process execution", icon: Settings }
  ];

  return (
    <>
      <SEOHead
        title="Microsoft Dynamics 365 Implementation Services | Smartborn"
        description="Expert Microsoft Dynamics 365 implementation and consulting services. Transform your business with integrated CRM, ERP, and business applications from certified consultants."
        keywords="Microsoft Dynamics 365, CRM implementation, ERP system, business applications, Power Platform, sales automation, customer service"
        canonical="/microsoft-dynamics-detail"
      />

      <div className="min-h-screen bg-background">
        <Navigation />

        <main className="pt-16">
          {/* Hero Section */}
          <section className="py-20 bg-gradient-to-br from-background via-background to-primary/5">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto text-center">
                <Badge className="mb-6 bg-secondary/10 text-secondary border-secondary/20">
                  Microsoft Experts
                </Badge>
                <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-primary bg-clip-text text-transparent">
                  Microsoft Dynamics 365
                </h1>
                <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                  Transform your business with integrated CRM and ERP solutions. Streamline sales, marketing, service, finance, and operations on a unified platform.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button size="lg" onClick={handleGetStarted} className="bg-gradient-primary hover:opacity-90">
                    Start Your Transformation
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                  {/* <Button size="lg" variant="outline" onClick={() => navigate("/")}>
                    View All Solutions
                  </Button> */}
                </div>
              </div>
            </div>
          </section>

          {/* Implementation Approach */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold mb-6 text-foreground">
                  Our Implementation Methodology
                </h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  Proven approach to deliver successful Dynamics 365 implementations with minimal business disruption
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {implementationPhases.map((phase, index) => (
                  <Card key={index} className="relative bg-gradient-card border-0 shadow-elegant">
                    <CardHeader className="text-center pb-4">
                      <div className="bg-gradient-primary text-white rounded-full w-12 h-12 flex items-center justify-center mx-auto mb-4 text-xl font-bold">
                        {index + 1}
                      </div>
                      <CardTitle className="text-lg font-semibold text-foreground">{phase.phase}</CardTitle>
                      <Badge variant="outline" className="mx-auto">{phase.duration}</Badge>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {phase.activities.map((activity, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                            <CheckCircle className={`h-4 w-4 ${idx % 2 === 0 ? 'text-primary shadow-glow' : 'text-secondary shadow-glow-secondary'} mt-0.5 flex-shrink-0`} />
                            {activity}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>

          {/* Dynamics 365 Modules */}
          <section className="py-20 bg-muted/30">
            <div className="container mx-auto px-4">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold mb-6 text-foreground">
                  Complete Dynamics 365 Suite
                </h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  Comprehensive business applications designed to work together seamlessly
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {dynamicsModules.map((module, index) => (
                  <Card key={index} className="group hover:shadow-elegant transition-all duration-300 bg-gradient-card border-0">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="bg-gradient-primary p-3 rounded-lg group-hover:shadow-glow transition-all duration-300">
                          <module.icon className="h-6 w-6 text-white" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-foreground mb-3 text-lg">
                            {module.name}
                          </h3>
                          <p className="text-muted-foreground text-sm leading-relaxed">
                            {module.description}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>

          {/* Key Benefits */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold mb-6 text-foreground">
                  Business Impact & ROI
                </h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  Measurable results delivered through strategic Dynamics 365 implementation
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {benefits.map((benefit, index) => (
                  <Card key={index} className="text-center bg-gradient-card border-0 shadow-elegant">
                    <CardContent className="p-8">
                      <benefit.icon className={`h-12 w-12 ${index % 2 === 0 ? 'text-primary shadow-glow' : 'text-secondary shadow-glow-secondary'} mx-auto mb-4`} />
                      <h3 className="font-semibold text-foreground mb-2 text-lg">
                        {benefit.metric}
                      </h3>
                      <p className="text-muted-foreground text-sm">
                        {benefit.value}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-20 bg-gradient-to-br from-primary/5 via-secondary/5 to-background">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto text-center">
                <h2 className="text-3xl md:text-4xl font-bold mb-6 text-foreground">
                  Ready to Transform Your Business?
                </h2>
                <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                  Partner with certified Dynamics 365 experts to unlock your organization's full potential with integrated business applications.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button size="lg" onClick={handleGetStarted} className="bg-gradient-primary hover:opacity-90">
                    Schedule Consultation
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                  {/* <Button size="lg" variant="outline" onClick={() => navigate("/")}>
                    Explore More Solutions
                  </Button> */}
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default MicrosoftDynamicsDetail;